// QuickView.js — Espiada rápida em um livro, sem sair do catálogo.
//
// Motivo: com 198 títulos, comparar dois livros custava ida e volta à página de
// produto e a perda da posição do scroll e dos filtros aplicados. Aqui a pessoa
// lê a sinopse, vê o preço, adiciona ao carrinho e continua de onde parou.
//
// O modal NÃO substitui a página de produto: ele mostra o suficiente para
// decidir e sempre oferece o caminho para a página completa (metadados,
// "leia um trecho", opiniões).
import React, { useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import { m, AnimatePresence, useReducedMotion } from "motion/react";
import BorderGlow from "../../ui/BorderGlow";
import NotaLivro from "../paginaProduto/NotaLivro";
import { CarrinhoContext } from "../../../services/context/CarrinhoContext";
import { useModal } from "../../../services/context/ModalContext";
import { camposDePreco } from "../../../utils/precoLivro";
import PrecoComDesconto from "../../ui/PrecoComDesconto";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

const precoValido = (p) => p && !isNaN(parseFloat(p)) && parseFloat(p) > 0;
const fmt = (v) => parseFloat(v).toFixed(2).replace(".", ",");

const QuickView = ({ livro, onClose }) => {
  const { adicionarItem } = useContext(CarrinhoContext);
  const { openModal } = useModal();
  const reduzirMovimento = useReducedMotion();

  // Esc fecha e o fundo não rola enquanto o modal está aberto
  useEffect(() => {
    if (!livro) return;
    const aoTeclar = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", aoTeclar);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.body.style.overflow = overflowAnterior;
    };
  }, [livro, onClose]);

  const digital = precoValido(livro?.preco) ? parseFloat(livro.preco) : null;
  // Título liberado pela editora: quem decide é o servidor (`copia_gratis_ativa`
  // já exige o PDF existir). Aqui ele troca o preço e o botão — sem isto, a
  // espiada de um livro grátis dizia "Disponível em físico", que é tudo o que
  // ela sabia responder para um preço zero.
  const copiaGratis = livro?.copia_gratis_ativa === true;
  const fisico = precoValido(livro?.precoImpresso ?? livro?.preco_impresso)
    ? parseFloat(livro.precoImpresso ?? livro.preco_impresso)
    : null;

  const autores = Array.isArray(livro?.autores)
    ? livro.autores.filter(Boolean).map((a) => (typeof a === "object" ? a.nome : a)).join(", ")
    : "";

  const adicionar = () => {
    if (!digital) return;
      // `formato` do item = QUAL VERSÃO está sendo comprada, e todo card vende
      // o digital (usa `livro.preco`; o impresso é `preco_impresso`, escolhido
      // só no PrecoCard). NUNCA repassar `livro.formato` aqui: ali o campo é o
      // tamanho do impresso ("16x23 (160x230)") e foi o que quebrou o checkout
      // em 20/08/2026. O CarrinhoContext também saneia, mas a intenção fica
      // explícita na origem.
      // A nota e o autor viajam junto com o item para o carrinho poder
      // mostrá-los sem uma requisição por linha. Quem já tinha itens no
      // carrinho antes disto não tem os campos, e lá a linha de estrelas
      // simplesmente não aparece (ver a guarda em CarrinhoPage.js).
    const item = {
      // O preco que vai para o carrinho e o do SERVIDOR (precoDeVenda le
      // `preco_com_desconto`). Mandar o cheio de um livro em destaque faz o
      // checkout recusar o pedido inteiro -- ver src/utils/precoLivro.js.
      // `preco_original` viaja junto para o carrinho poder riscar o valor antigo.
      id: livro.id,
      titulo: livro.titulo,
      ...camposDePreco(livro),
      image: livro.image,
      autores: livro.autores,
      formato: "ebook",
      quantidade: 1,
      avaliacoes_avg_nota: livro.avaliacoes_avg_nota,
      avaliacoes_count: livro.avaliacoes_count,
    };
    adicionarItem(item);
    openModal(item);
    onClose();
  };

  return (
    <AnimatePresence>
      {livro && (
        <m.div
          className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduzirMovimento ? 0 : 0.2 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={`Detalhes de ${livro.titulo}`}
        >
          <m.div
            className="relative w-full max-w-[720px] max-h-[88vh] overflow-y-auto"
            initial={reduzirMovimento ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={reduzirMovimento ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: reduzirMovimento ? 0 : 0.24, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <BorderGlow
              raio={4}
              className="bloco
                         shadow-[0_24px_70px_rgba(0,0,0,0.5)]"
            >
            <button
              onClick={onClose}
              aria-label="Fechar"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/40 border border-line
                         flex items-center justify-center text-paper hover:bg-black/70 transition-colors"
            >
              <i className="bi bi-x-lg text-sm" />
            </button>

            <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-5 p-5 md:p-6">
              {/* Capa */}
              <div className="rounded-xl overflow-hidden bg-insigne-navy-deep aspect-[3/4] max-w-[200px] mx-auto sm:mx-0 w-full">
                <img
                  src={`${API_URL}public/${livro.image}`}
                  alt={livro.titulo}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Dados */}
              <div className="min-w-0">
                <h3 className="text-lg md:text-xl font-bold text-white leading-snug">{livro.titulo}</h3>
                {autores && <p className="text-sm text-insigne-orange mt-1">{autores}</p>}
                {/* A nota já veio junto com o livro na listagem do catálogo —
                    o modal não busca nada a mais para mostrá-la. */}
                <NotaLivro livro={livro} size="text-xs" className="mt-2" />

                {Array.isArray(livro.categorias) && livro.categorias.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {livro.categorias.slice(0, 3).map((c, i) => (
                      <span key={i} className="px-2.5 py-1 rounded text-xs bg-insigne-navy-mid text-white/70">
                        {typeof c === "string" ? c : c?.nome}
                      </span>
                    ))}
                  </div>
                )}

                {livro.sinopse && (
                  <p className="text-sm text-white/70 leading-relaxed mt-3 line-clamp-6">{livro.sinopse}</p>
                )}

                {/* Preços. `items-end`: quando o e-book está em promoção ele
                    ganha a linha do riscado em cima, e é o NÚMERO dos dois
                    formatos que precisa ficar na mesma altura, não o rótulo.

                    O e-book usa o desenho de toda vitrine (PrecoComDesconto):
                    esta espiada mostrava o preço CHEIO e o botão logo abaixo
                    mandava para o carrinho o com desconto. */}
                <div className="mt-4 flex flex-wrap items-end gap-x-6 gap-y-2">
                  {copiaGratis ? (
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-white/45">E-book</p>
                      <p className="text-insigne-promo text-2xl font-bold leading-tight">Grátis</p>
                    </div>
                  ) : digital && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-white/45">E-book</p>
                      <PrecoComDesconto
                        livro={livro}
                        numero="text-2xl"
                        reservarRiscado={false}
                        className="text-white"
                      />
                    </div>
                  )}
                  {fisico && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-white/45">Impresso</p>
                      <p className="text-white">
                        <span className="text-xs align-top">R$</span>{" "}
                        <span className="text-2xl font-bold">{fmt(fisico)}</span>
                      </p>
                    </div>
                  )}
                </div>

                {/* Ações */}
                <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
                  {copiaGratis ? (
                    // Sem botão de carrinho: não há o que comprar. A página do
                    // livro é quem tem o "Ler agora" e o "Baixar PDF" — e é
                    // também onde a pessoa entra na conta, se ainda não entrou.
                    <Link
                      to={main + "livros/" + livro.id}
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                      className="flex-1 flex items-center justify-center gap-2 rounded-controle bg-insigne-orange
                                 text-paper font-semibold text-sm py-2.5 hover:bg-insigne-orange-dark transition-colors"
                    >
                      <i className="bi bi-book" /> Ler ou baixar
                    </Link>
                  ) : digital ? (
                    <button
                      onClick={adicionar}
                      className="flex-1 rounded-controle bg-insigne-orange text-paper font-semibold text-sm py-2.5
                                 hover:bg-insigne-orange-dark transition-colors"
                    >
                      Adicionar ao Carrinho
                    </button>
                  ) : (
                    <span className="flex-1 text-center rounded-controle border-2 border-line text-white/55 text-sm py-2.5">
                      Disponível em físico
                    </span>
                  )}
                  <Link
                    to={main + "livros/" + livro.id}
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="flex-1 text-center rounded-controle border-2 border-insigne-orange text-insigne-orange
                               font-semibold text-sm py-2.5 hover:bg-insigne-orange hover:text-paper transition-colors"
                  >
                    Ver página completa
                  </Link>
                </div>
              </div>
            </div>
            </BorderGlow>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default QuickView;
